import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { StaffWorkspace } from "../../../app/session";
import { EinrichtungFehlt } from "../../../lib/einrichtung";
import { api } from "../../../data";
import { useAgencyTerms, useBrands, useDeals, useFxRates, useInvoices, useLineItems, useOrgSettings, usePeriods, useTalents } from "../../../data/queries";
import { useOrgConfig } from "../../../model/org-config";
import { useTarifStand } from "../../../components/Tarif";
import { today as todayIn } from "../../../lib/format";
import {
  fetchArchiveCheck, fetchBookings, fetchBrand, fetchBrandContacts, fetchBrandNote, fetchContracts, fetchIg, fetchIgOverview, fetchInvitationDays,
  fetchInvitations, fetchLinks, fetchPayout, fetchPlan, fetchPresence, fetchTalent, fetchTalentNote, fetchTeamInfo,
} from "./data";
import { prepareBase, type Base, type BrandFull, type DealFull, type PresenceRow, type TalentFull } from "./model";

/** Eigene Abfragen tragen „stammdaten“ im Schlüssel, damit sie den Cache anderer Gruppen nicht mit anderen Spalten füllen. */
const KEY = "stammdaten";

/**
 * Alles, was die Stammdaten-Seiten gemeinsam brauchen: die gemeinsamen Hooks (data/queries.ts) plus eigene Abfragen.
 * `noAccess`: bleibt false. Jede Person im Team liest org_settings (0004 settings_read); fehlt die Zeile, ist die Einrichtung
 * offen (Probelauf H1) und `error` ist EinrichtungFehlt: ErrorState zeigt dann den Weg zur Einrichtung.
 */
export function useBase(ws: StaffWorkspace) {
  const orgId = ws.org.org_id;
  const q = {
    settings: useOrgSettings(orgId), cfg: useOrgConfig(orgId), talents: useTalents(orgId), brands: useBrands(orgId), deals: useDeals(orgId),
    periods: usePeriods(orgId), extras: useLineItems(orgId), terms: useAgencyTerms(orgId), invoices: useInvoices(orgId),
    // Kurse wie auf der Übersicht (SAAS-R2-1): Umsätze in anderer Währung umrechnen
    fx: useFxRates(),
    bookings: useQuery({ queryKey: ["bookings", orgId, KEY], queryFn: () => fetchBookings(api, orgId) }),
    links: useQuery({ queryKey: ["talent_user_links", orgId, KEY], queryFn: () => fetchLinks(api, orgId) }),
    invitations: useQuery({ queryKey: ["invitations", orgId, KEY], queryFn: () => fetchInvitations(api, orgId) }),
    contracts: useQuery({ queryKey: ["contracts", orgId, KEY], queryFn: () => fetchContracts(api, orgId) }),
    // Anwesenheit ist ein Zusatz: fehlt die Sicht, bleibt die Seite trotzdem nutzbar
    presence: useQuery({ queryKey: ["talent_presence", orgId, KEY], queryFn: () => fetchPresence(api, orgId).catch(() => [] as PresenceRow[]) }),
  };
  // Kurse sind ein Zusatz (Umrechnung, SAAS-R2-1): ohne sie baut die Seite auf, Fremdwährungen stehen dann als „ohne Kurs“
  const list = Object.values(q).filter((x) => x !== q.fx);
  const loading = list.some((x) => x.isPending);
  // Probelauf H1: Einstellungszeile fehlt (halb eingerichtete Agentur): Hinweis zur Einrichtung statt „nicht freigegeben“
  const error = list.find((x) => x.error)?.error ?? (!q.settings.isPending && q.settings.data === null ? new EinrichtungFehlt("org_settings") : null);
  const today = todayIn(ws.org.timezone);
  const settings = q.settings.data;
  const base = useMemo<Base | null>(() => {
    if (loading || error || !settings || !q.cfg.data) return null;
    return prepareBase({
      today, nowMs: Date.now(), currency: settings.currency, settings, cfg: q.cfg.data,
      talents: (q.talents.data ?? []) as TalentFull[], brands: (q.brands.data ?? []) as BrandFull[], deals: (q.deals.data ?? []) as DealFull[],
      periods: q.periods.data ?? [], extras: q.extras.data ?? [], terms: q.terms.data ?? [], invoices: q.invoices.data ?? [],
      bookings: q.bookings.data ?? [], links: q.links.data ?? [], invitations: q.invitations.data ?? [], contracts: q.contracts.data ?? [],
      presence: q.presence.data ?? [], fx: q.fx.data ?? [],
    });
    // Abhängigkeiten sind die geladenen Daten selbst
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error, settings, today, q.cfg.data, q.talents.data, q.brands.data, q.deals.data, q.periods.data, q.extras.data, q.terms.data,
      q.invoices.data, q.bookings.data, q.links.data, q.invitations.data, q.contracts.data, q.presence.data, q.fx.data]);
  return {
    base,
    error,
    loading: loading || (!error && settings !== null && !base),
    noAccess: false,
    retry: () => list.forEach((x) => x.error && void x.refetch()),
    refresh: () => list.forEach((x) => void x.refetch()),
  };
}

/** Tarif (nur für den Admin lesbar). */
export const usePlan = (ws: StaffWorkspace) =>
  useQuery({ queryKey: ["subscriptions", ws.org.org_id, KEY], enabled: ws.isChef, queryFn: () => fetchPlan(api, ws.org.org_id) });
export const useInvitationDays = (orgId: string) =>
  useQuery({ queryKey: ["platform_defaults", orgId, "invitation_days", KEY], queryFn: () => fetchInvitationDays(api, orgId) });
export const useTalentNote = (orgId: string, talentId: string) =>
  useQuery({ queryKey: ["talent_internal_notes", orgId, talentId, KEY], queryFn: () => fetchTalentNote(api, talentId) });
/** Auszahlungsdaten nur abfragen, wenn das Recht besteht (die Datenbank prüft es zusätzlich). */
export const usePayout = (orgId: string, talentId: string, allowed: boolean) =>
  useQuery({ queryKey: ["talent_payout_details", orgId, talentId, KEY], enabled: allowed, queryFn: () => fetchPayout(api, talentId) });
export const useBrandNote = (orgId: string, brandId: string | null) =>
  useQuery({ queryKey: ["brand_internal_notes", orgId, brandId, KEY], enabled: !!brandId, queryFn: () => fetchBrandNote(api, brandId!) });
export const useBrandContacts = (orgId: string) =>
  useQuery({ queryKey: ["brand_contacts", orgId, KEY], queryFn: () => fetchBrandContacts(api, orgId) });
export const useIg = (orgId: string, talentId: string) =>
  useQuery({ queryKey: ["ig_accounts", orgId, talentId, KEY], queryFn: () => fetchIg(api, orgId, talentId) });
export const useIgOverview = (orgId: string) =>
  useQuery({ queryKey: ["ig_accounts", orgId, "uebersicht", KEY], queryFn: () => fetchIgOverview(api, orgId) });
/** Bearbeiten-Dialoge laden den Eintrag frisch (kein Cache), damit nichts mit veralteten Werten überschrieben wird. */
export const useFreshTalent = (orgId: string, talentId: string) =>
  useQuery({ queryKey: ["talents", orgId, talentId, "frisch", KEY], queryFn: () => fetchTalent(api, talentId), staleTime: 0, gcTime: 0 });
export const useFreshBrand = (orgId: string, brandId: string | null) =>
  useQuery({ queryKey: ["brands", orgId, brandId, "frisch", KEY], enabled: !!brandId, queryFn: () => fetchBrand(api, brandId!), staleTime: 0, gcTime: 0 });
/** Wer betreut welchen Creator (für Liste, Filter „Meine Creator“ und Profil). */
export const useTeamInfo = (ws: StaffWorkspace) =>
  useQuery({ queryKey: ["manager_talent_assignments", ws.org.org_id, "betreut", KEY], queryFn: () => fetchTeamInfo(api, ws.org.org_id, api.session()?.userId ?? null) });
/** Tarifmodule für alle Mitarbeiter (nicht nur den Admin): „Instagram im Tarif?“ Gemeinsamer Stand mit components/Tarif.tsx. */
export const useEntitlements = (orgId: string) => useTarifStand(orgId);
export const useArchiveCheck = (orgId: string, talentId: string, enabled: boolean) =>
  useQuery({ queryKey: ["talents", orgId, talentId, "archivieren", KEY], enabled, queryFn: () => fetchArchiveCheck(api, talentId), staleTime: 0 });
